import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const MatrixContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  zIndex: 0,
  opacity: theme.palette.mode === 'dark' ? 0.3 : 0.15,
  pointerEvents: 'none'
}));

const MatrixCanvas = styled('canvas')({
  width: '100%',
  height: '100%',
  display: 'block'
});

const MatrixBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Matrix characters - mix of code symbols and characters
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%^&*(){}[]<>/\\|`~+-=_';
    const codeSnippets = [
      'const', 'function', 'return', 'if', 'else', 'for', 'while', 'class',
      'import', 'export', 'async', 'await', 'try', 'catch', 'throw',
      'React', 'useState', 'useEffect', 'props', 'state', 'component',
      'blockchain', 'smart', 'contract', 'web3', 'ethereum', 'solidity',
      'AI', 'ML', 'neural', 'network', 'algorithm', 'data', 'model'
    ];

    const fontSize = 14;
    const columns = Math.floor(canvas.width / fontSize);
    const drops: number[] = [];

    // Initialize drops
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * canvas.height;
    }

    const draw = () => {
      // Semi-transparent black background for trail effect
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Green text
      ctx.fillStyle = '#00ff41';
      ctx.font = `${fontSize}px 'Courier New', monospace`;

      for (let i = 0; i < drops.length; i++) {
        // Randomly choose between single character and code snippet
        const useCodeSnippet = Math.random() < 0.3;
        let text: string;
        
        if (useCodeSnippet) {
          text = codeSnippets[Math.floor(Math.random() * codeSnippets.length)];
        } else {
          text = chars[Math.floor(Math.random() * chars.length)];
        }

        const x = i * fontSize;
        const y = drops[i] * fontSize;

        ctx.fillText(text, x, y);

        // Reset drop to top randomly
        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }

        // Move drop down
        drops[i] += 0.5 + Math.random() * 0.5;
      }
    };

    const interval = setInterval(draw, 50);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <MatrixContainer>
      <MatrixCanvas ref={canvasRef} />
    </MatrixContainer>
  );
};

export default MatrixBackground;